<template>
  <div class="history-record-page">
    <!-- 标题区域 -->
    <div class="page-header">
      <h1 class="page-title">历史记录</h1>
      <p class="page-desc">
        查看并恢复法律检索、文书生成、合同审查、文件审查和企业查询任务。
      </p>
    </div>

    <!-- 筛选搜索栏（改用普通Input自定义后置图标，无原生search按钮） -->
    <div class="filter-bar">
      <div class="search-wrap">
        <a-input
          v-model:value="searchKey"
          class="search-input"
          placeholder="搜索名称、案由"
          allow-clear
          @keyup.enter="handleSearch"
        >
          <template #suffix>
            <img
              src="@/assets/icons/i-search.png"
              style="width: 20px; height: 20px; cursor: pointer"
              @click="handleSearch"
            />
          </template>
        </a-input>
      </div>
      <div class="filter-right">
        <a-select
          v-model:value="typeVal"
          class="type-select"
          @change="handleSearch"
        >
          <a-select-option
            v-for="item in typeList"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
        <a-select
          v-model:value="timeVal"
          class="time-select"
          @change="handleSearch"
        >
          <a-select-option
            v-for="item in timeList"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 记录列表 -->
    <div class="record-list">
      <div v-for="item in recordList" :key="item.id" class="record-card">
        <div class="card-left">
          <div class="file-icon">
            <img
              src="@/assets/icons/i-word.png"
              style="width: 53px; height: 56px; margin-right: 8px"
            />
          </div>
          <div class="card-info">
            <div class="record-name">{{ item.name }}</div>
            <div class="record-sub">
              {{ item.tag }} · {{ item.desc }}
              <span class="create-time">{{ item.createTime }}</span>
            </div>
          </div>
        </div>
        <div class="card-btn-group">
          <a-button class="del-btn" @click="handleDelete(item.id)"
            >删除</a-button
          >
          <a-button class="detail-btn" @click="handleDetail(item)"
            >详情</a-button
          >
        </div>
      </div>
    </div>
    <!-- 分页：修正判断条件，读取pagination.total，总数>10才显示 -->
    <div class="pagination-bar" v-show="pagination.total > 10">
      <a-pagination
        v-model:current="pagination.pageNum"
        :total="pagination.total"
        :pageSize="pagination.pageSize"
        show-size-changer
        :show-total="(total) => `共 ${total} 条`"
        @change="handlePageChange"
      >
        <template #buildOptionText="option">
          {{
            option.value === pagination.pageSize
              ? `${option.value} 条/页`
              : `${option.value} 条/页`
          }}
        </template>
      </a-pagination>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted } from "vue";
import { message, Modal } from "ant-design-vue";
import {
  getOperationTypes,
  getPageData,
  deleteHistoryRecord,
  GetGroupByRecordId,
} from "@/api/history.js";
// 从父组件注入 goCase 方法
const goCase = inject("goCase");

// 搜索框绑定值
const searchKey = ref("");
// 下拉选中值
const typeVal = ref("");
const timeVal = ref(0);

// 类型下拉数组配置
const typeList = ref([{ label: "全部类型", value: "" }]);

// 时间下拉数组配置
const timeList = ref([
  { label: "全部", value: 0 },
  { label: "近7天", value: 7 },
  { label: "近30天", value: 30 },
  { label: "近90天", value: 90 },
]);

// 分页状态
const pagination = ref({
  pageNum: 1,
  pageSize: 10,
  total: 0,
});

// 列表数据
const recordList = ref([]);

// 获取操作类型
const fetchOperationTypes = async () => {
  try {
    const res = await getOperationTypes();
    if (res?.data) {
      const list = res.data.map((item) => ({
        label: item.name,
        value: item.value,
      }));
      typeList.value = [{ label: "全部类型", value: "" }, ...list];
    }
  } catch (err) {
    console.error("获取操作类型失败", err);
  }
};

// 获取历史记录列表
const fetchRecordList = async () => {
  try {
    const params = {
      pageSize: pagination.value.pageSize,
      pageNum: pagination.value.pageNum,
      parameter: {
        operationType: typeVal.value,
        recentDays: timeVal.value,
        titleKey: searchKey.value,
      },
    };
    const res = await getPageData(params);
    if (res?.data) {
      recordList.value = (res.data.data || []).map((item) => ({
        id: item.id,
        name: item.title || item.requestParams || "",
        tag: item.operationName || "",
        desc: item.isSuccess ? "成功" : "失败",
        createTime: item.createTime || "",
      }));
      pagination.value.total = res.data.total || 0;
    }
  } catch (err) {
    console.error("获取历史记录失败", err);
  }
};

// 搜索事件
const handleSearch = () => {
  pagination.value.pageNum = 1;
  fetchRecordList();
};

// 分页变化
const handlePageChange = (page, pageSize) => {
  pagination.value.pageNum = page;
  pagination.value.pageSize = pageSize;
  fetchRecordList();
};

// 删除事件
const handleDelete = async (id) => {
  Modal.confirm({
    title: "确认删除",
    content: "确定要删除这条记录吗？删除后不可恢复。",
    okText: "确定",
    cancelText: "取消",
    onOk: async () => {
      try {
        await deleteHistoryRecord(id);
        message.success("删除成功");
        fetchRecordList();
      } catch (err) {
        console.error("删除失败", err);
        message.error("删除失败");
      }
    },
  });
};

// 查看详情事件
const handleDetail = async (row) => {
  try {
    const res = await GetGroupByRecordId(row.id);
    const detail = res?.data || res?.result;

    if (!detail) {
      message.error("获取详情失败：数据为空");
      return;
    }

    // 根据 operationName 跳转不同路由
    const operationName = row.tag;
    const keyMap = {
      类案检索: "aiselect",
      起诉状生成: "aikmssmg",
      AI对话: "my-ai-chat",
    };
    const targetKey = keyMap[operationName];
    if (targetKey) {
      goCase(targetKey, detail);
    } else {
      message.info("暂无该类型的详情跳转");
    }
  } catch (err) {
    console.error("获取详情失败", err);
  }
};

onMounted(() => {
  fetchOperationTypes();
  fetchRecordList();
});
</script>

<style scoped lang="less">
/* 截图1:1精准配色变量 */
@pageBg: #f5f7fc;
@cardWhite: #ffffff;
@textMain: #1d2129;
@textGray: #86909c;
@bluePrimary: #4080ff;
@borderLine: #e5e7eb;
@searchRadius: 6px;

.history-record-page {
  width: 100%;
  margin-top: 24px;
  background: @pageBg;
  /* 列表页需要"分页器固定 + 列表内部滚动"：页面锁定为视口高度减去
     外壳固定占用（64px 固定头部 + 上下 24px 边距 = 112px），其余分辨率自适应；
     内部用 flex 分配：标题/筛选/分页固定，列表 flex:1 滚动。
     注意：此写法仅限需要固定分页器的列表页，其他页面保持 body 自然滚动 */
  height: calc(100vh - 112px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.page-header {
  flex-shrink: 0;
  margin-bottom: 16px;
  .page-title {
    font-weight: 600;
    font-size: 34px;
    color: #12213f;
    margin: 0 0 6px;
  }
  .page-desc {
    font-weight: 400;
    font-size: 15px;
    color: #64748f;
    line-height: 26px;
  }
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: @cardWhite;
  padding: 16px 22px;
  border-radius: 8px;
  margin-bottom: 20px;
  flex-shrink: 0;

  .search-wrap {
    width: 320px;
    // 直接控制输入框整体圆角，无多余按钮干扰
    :deep(.ant-input-affix-wrapper) {
      border-radius: @searchRadius !important;
      border: 1px solid @borderLine;
    }
  }

  .filter-right {
    display: flex;
    gap: 12px;
    .type-select,
    .time-select {
      width: 112px;
      :deep(.ant-select-selector) {
        border-radius: @searchRadius !important;
      }
    }
  }
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* 吃满页面剩余高度（标题/筛选栏/分页器之外），仅列表内部滚动 */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.record-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: @cardWhite;
  padding: 18px 22px;
  border-radius: 12px;
  gap: 24px;
  border: 1px solid @borderLine;
  .card-left {
    display: flex;
    align-items: center;
    gap: 14px;
    .file-icon {
      width: 42px;
      height: 42px;
      border-radius: 9px;
      background: rgba(64, 128, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .card-info {
      .record-name {
        font-family: PingFangSC, PingFang SC;
        font-weight: 600;
        font-size: 16px;
        color: #333333;
        line-height: 1.5;
        margin-bottom: 5px;
      }
      .record-sub {
        font-family: PingFangSC, PingFang SC;
        font-weight: 400;
        font-size: 14px;
        color: #5f6a7a;
        line-height: 16px;
        .create-time {
          margin-left: 12px;
        }
      }
    }
  }
  .card-btn-group {
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
    flex-shrink: 0;
    .del-btn,
    .detail-btn {
      flex-shrink: 0;
      white-space: nowrap;
    }
  }
}

/* 按钮单独加class强覆盖，规避样式穿透失效 */
:deep(.del-btn) {
  border-color: #dcdfe6;
  font-weight: 400;
  font-size: 16px;
  height: 32px;
  line-height: 22px;
  color: #263957 !important;
  border-radius: 12px !important;
}
:deep(.detail-btn) {
  background: #eaf8f3 !important;
  border-radius: 12px !important;
  border: 1px solid #d9e0ed !important;
  font-weight: 400;
  height: 32px;
  line-height: 22px;
  font-size: 16px !important;
  color: #178760 !important;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  flex-shrink: 0;
}
</style>
